import { isEqual } from 'lodash';
import { useMemo, useRef } from 'react';
import {
  type DefaultValues,
  type FieldValues,
  useForm,
  type UseFormReturn,
} from 'react-hook-form';

type CompleteDefaultValues<T extends FieldValues> = {
  [K in keyof T]: T[K];
};

/**
 * Custom hook that performs deep comparison memoization for objects.
 *
 * @purpose Prevents unnecessary re-renders when object references change but values remain the same.
 * This is critical for react-hook-form's `values` prop, which forces form state updates on every change.
 *
 * @usage Used to stabilize `defaultValues` in `useSectionForm` to prevent form state resets
 * when parent components re-render with new object references but identical values.
 *
 * @warning Performs deep equality check (lodash.isEqual) on every render. May impact performance
 * with large objects. Consider using React.memo or selective memoization for parent components
 * if performance issues arise.
 *
 * @example
 * const stableConfig = useDeepCompareMemo({ name: 'John', age: 30 });
 * // stableConfig reference only changes when values actually differ
 */
const useDeepCompareMemo = <T>(value: T): T => {
  'use no memo';
  const ref = useRef<T>(value);

  // Use useMemo to prevent running comparison if value reference hasn't changed
  return useMemo(() => {
    try {
      if (!isEqual(value, ref.current)) {
        ref.current = value;
      }
    } catch (error) {
      // Fallback to new value if comparison fails (e.g., circular references)
      console.error('Deep comparison failed:', error);
      ref.current = value;
    }
    return ref.current;
  }, [value]);
};

/**
 * Custom hook for managing form state in benefit configuration sections.
 *
 * @purpose Wraps react-hook-form's useForm with stable default values to handle
 * scenarios where benefit config objects change between versions.
 *
 * @problem react-hook-form caches defaultValues on first render and doesn't
 * automatically update form state when defaults change. When switching benefit
 * versions, form state would persist old values.
 *
 * @solution Uses deep comparison to detect actual value changes and forces form
 * state updates via the `values` prop when defaults genuinely change.
 *
 * @param defaults - Initial form values for the benefit configuration section
 * @returns React Hook Form instance with stabilized and synchronized state
 *
 * @example
 * const form = useSectionForm({ benefitName: 'Health Insurance', cost: 100 });
 */
export const useSectionForm = <TFieldValues extends FieldValues>(
  defaults: CompleteDefaultValues<TFieldValues>
): UseFormReturn<TFieldValues> => {
  const stableDefaults = useDeepCompareMemo(defaults);

  const form = useForm<TFieldValues>({
    defaultValues: stableDefaults as DefaultValues<TFieldValues>,
    // Force form state to update when defaults change (workaround for react-hook-form caching)
    values: stableDefaults,
    // Preserve user edits if the values prop re-syncs while the form is dirty
    resetOptions: { keepDirtyValues: true },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldFocusError: true,
    criteriaMode: 'firstError',
    shouldUnregister: false,
  });

  return form;
};

export default useSectionForm;
